import React, { useCallback } from 'react';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import { useSelector } from 'store';
import { useTranslation } from 'hooks/useTranslation';
import { Paper } from 'features/app/components/Paper';
import BaseButton from 'features/app/components/BaseButton';
import { GiftOrderSummary } from 'features/dashboard/components/GiftOrderSummary';
import { clearOrdersState, createBulkOrderRequest } from 'features/dashboard/redux/actions';

import { ReactComponent as DoneIcon } from 'assets/icons/done-icon.svg';

import { useStyles } from './styles';
import UndeliverableEmailsBlock from 'features/dashboard/components/ProcessBulkOrder/UndeliverableEmailsBlock';

interface ProcessBulkOrderProps {
  selectedFileName: string;
}

export const ProcessBulkOrder = ({ selectedFileName }: ProcessBulkOrderProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation('dashboard');
  const { orderSummary, orders } = useSelector(state => state.dashboard.orders);
  const undeliverableEmails =
    useSelector(state => state.dashboard.orders.bulkEmailsValidation)?.filter(
      ({ result }) => !['deliverable', 'catch_all', 'unknown'].includes(result)
    ) || [];

  const handleProcessOrderBtnClick = useCallback(() => {
    orders && dispatch(createBulkOrderRequest(orders));
  }, [dispatch, orders]);

  const handleCancelOrderBtnClick = useCallback(() => {
    dispatch(clearOrdersState());
  }, [dispatch]);

  return (
    <Paper borderRadius={20} className={classes.processBulkOrderContainer}>
      <Box flex={1} display="flex" flexDirection="column" className={classes.processBulkOrderContent}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginBottom={15}
          className={classes.uploadedFileStatusContainer}
        >
          <Box paddingRight={15}>
            <DoneIcon />
          </Box>
          <Box className={classes.uploadedFileTitleWrapper}>
            <Typography variant="h3" className={classes.uploadedFileTitle}>
              {`${t('fileProcessedSuccessfully')} ${selectedFileName}`}
            </Typography>
          </Box>
          <Box alignSelf="flex-end">
            <BaseButton
              variant="text"
              maxWidth={80}
              onClick={handleCancelOrderBtnClick}
              className={classes.cancelOrderBtn}
            >
              {t('cancel', { ns: 'common' })}
            </BaseButton>
          </Box>
        </Box>
        {orderSummary && <GiftOrderSummary size="medium" summary={orderSummary} />}
        {!_.isEmpty(undeliverableEmails) && (
          <UndeliverableEmailsBlock size={'medium'} undeliverableEmails={undeliverableEmails} />
        )}
        <Box display="flex" justifyContent="center" paddingTop={32.5}>
          <BaseButton
            maxWidth={269}
            textTransform="uppercase"
            onClick={handleProcessOrderBtnClick}
            className={classes.processOrderBtn}
          >
            {t('processOrder')}
          </BaseButton>
        </Box>
      </Box>
    </Paper>
  );
};
